import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { analyticsService } from '../../services/api/knowledge/analyticsService';
import { GASettings, GASettingInput } from '../../types/analytics';

interface GASettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  existingSettings?: GASettings | null;
  onSuccess?: () => void;
}

const GASettingsModal: React.FC<GASettingsModalProps> = ({
  isOpen,
  onClose,
  productId,
  existingSettings,
  onSuccess,
}) => {
  const [propertyId, setPropertyId] = useState<string>('');
  const [credentialsJson, setCredentialsJson] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showPropertyIdHelp, setShowPropertyIdHelp] = useState<boolean>(false);
  const [showCredentialsHelp, setShowCredentialsHelp] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{
    propertyId?: string;
    credentialsJson?: string;
  }>({});

  // Load existing settings when modal opens
  useEffect(() => {
    if (isOpen && existingSettings) {
      setPropertyId(existingSettings.ga4_property_id);
      // Note: credentials are encrypted and not returned from API for security
      setCredentialsJson('');
    } else if (isOpen && !existingSettings) {
      // Reset form for new settings
      setPropertyId('');
      setCredentialsJson('');
    }

    // Reset errors when modal opens
    if (isOpen) {
      setError('');
      setValidationErrors({});
    }
  }, [isOpen, existingSettings]);

  const validateForm = (): boolean => {
    const errors: { propertyId?: string; credentialsJson?: string } = {};

    if (!propertyId.trim()) {
      errors.propertyId = 'GA4 Property IDは必須です';
    }

    // Only validate credentials if it's a new setting or if credentials are provided
    if (!existingSettings && !credentialsJson.trim()) {
      errors.credentialsJson = 'Service Account認証情報は必須です';
    } else if (credentialsJson.trim()) {
      try {
        const parsed = JSON.parse(credentialsJson);
        if (!parsed.type || !parsed.project_id || !parsed.private_key || !parsed.client_email) {
          errors.credentialsJson = '有効なService Account JSONではありません';
        }
      } catch (e) {
        errors.credentialsJson = 'JSONの形式が無効です';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data: GASettingInput = {
        ga4_property_id: propertyId.trim(),
      };

      // Only include credentials if provided (for update or new)
      if (credentialsJson.trim()) {
        data.service_account_credentials = credentialsJson.trim();
      }

      await analyticsService.saveSettings(productId, data);

      // Success
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('Failed to save GA settings:', err);
      const errorMessage = err.response?.data?.message || err.message || 'GA設定の保存に失敗しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setCredentialsJson(content);

      // Clear previous validation error
      setValidationErrors(prev => ({ ...prev, credentialsJson: undefined }));
    };
    reader.onerror = () => {
      setError('ファイルの読み込みに失敗しました');
    };
    reader.readAsText(file);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Google Analytics設定">
      <form onSubmit={handleSubmit} className="ga-settings-form" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {error && (
          <div className="error-message flex items-center justify-between" style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        <div className="form-group flex flex-col gap-1.5" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <label htmlFor="propertyId" style={{ fontWeight: 500, margin: 0 }}>
              GA4 Property ID <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowPropertyIdHelp(!showPropertyIdHelp)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: '#2563eb',
                backgroundColor: 'transparent',
                border: '1px solid #2563eb',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {showPropertyIdHelp ? '閉じる' : '取得方法を見る'}
            </button>
          </div>

          {showPropertyIdHelp && (
            <div style={{
              padding: '12px',
              marginBottom: '12px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bfdbfe',
              borderRadius: '4px',
              fontSize: '13px',
              lineHeight: '1.6'
            }}>
              <strong style={{ display: 'block', marginBottom: '8px', color: '#1e40af' }}>
                GA4 Property IDの取得手順：
              </strong>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Google Analytics 4にログイン</li>
                <li>左下の「管理」（歯車アイコン）をクリック</li>
                <li>「プロパティ」列で対象のプロパティを選択</li>
                <li>「プロパティ設定」をクリック</li>
                <li>「プロパティID」の数字（例: 123456789）をコピー</li>
              </ol>
            </div>
          )}

          <input
            type="text"
            id="propertyId"
            value={propertyId}
            onChange={(e) => {
              setPropertyId(e.target.value);
              setValidationErrors(prev => ({ ...prev, propertyId: undefined }));
            }}
            placeholder="例: 123456789"
            disabled={loading}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: validationErrors.propertyId ? '1px solid #e53e3e' : '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          {validationErrors.propertyId && (
            <span style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.propertyId}
            </span>
          )}
        </div>

        <div className="form-group flex flex-col gap-1.5" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <label htmlFor="credentialsFile" style={{ fontWeight: 500, margin: 0 }}>
              Service Account認証情報
              {!existingSettings && <span style={{ color: '#e53e3e' }}> *</span>}
            </label>
            <button
              type="button"
              onClick={() => setShowCredentialsHelp(!showCredentialsHelp)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: '#2563eb',
                backgroundColor: 'transparent',
                border: '1px solid #2563eb',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {showCredentialsHelp ? '閉じる' : '取得方法を見る'}
            </button>
          </div>

          {showCredentialsHelp && (
            <div style={{
              padding: '12px',
              marginBottom: '12px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bfdbfe',
              borderRadius: '4px',
              fontSize: '13px',
              lineHeight: '1.6'
            }}>
              <strong style={{ display: 'block', marginBottom: '8px', color: '#1e40af' }}>
                Service Account認証情報の取得手順：
              </strong>
              <ol style={{ margin: 0, paddingLeft: '20px', marginBottom: '12px' }}>
                <li>Google Cloud Console（console.cloud.google.com）にアクセス</li>
                <li>対象のプロジェクトを選択</li>
                <li><strong>「APIとサービス」→「ライブラリ」をクリック</strong></li>
                <li><strong>「Google Analytics Data API」を検索して有効化</strong></li>
                <li>「IAMと管理」→「サービスアカウント」をクリック</li>
                <li>「サービスアカウントを作成」をクリック</li>
                <li>名前と説明を入力して「作成」</li>
                <li>ロールで「閲覧者」を選択して「続行」</li>
                <li>「完了」をクリック</li>
                <li>作成したサービスアカウントをクリック</li>
                <li>「キー」タブ→「鍵を追加」→「新しい鍵を作成」</li>
                <li>「JSON」を選択して「作成」</li>
                <li>ダウンロードされたJSONファイルを使用</li>
              </ol>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#92400e', fontWeight: 500 }}>
                  ⚠️ 重要: Google Analytics Data APIの有効化は必須です
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#92400e' }}>
                  API有効化後、システムに反映されるまで2-5分程度かかります
                </p>
              </div>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '4px',
                marginTop: '8px'
              }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#991b1b', fontWeight: 500 }}>
                  🔑 重要: サービスアカウントへの権限付与が必要です
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#991b1b' }}>
                  JSONファイルをダウンロードした後、以下の手順を実行してください：
                </p>
                <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '11px', color: '#991b1b' }}>
                  <li>JSONファイル内の「client_email」の値（例: xxx@xxx.iam.gserviceaccount.com）をコピー</li>
                  <li>Google Analytics 4（analytics.google.com）にアクセス</li>
                  <li>左下の「管理」→「プロパティのアクセス管理」をクリック</li>
                  <li>右上の「+」→「ユーザーを追加」をクリック</li>
                  <li>コピーしたメールアドレスを入力</li>
                  <li>「閲覧者」ロールを選択して「追加」</li>
                </ol>
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#991b1b', fontWeight: 500 }}>
                  この手順を完了しないとデータ取得ができません
                </p>
              </div>
            </div>
          )}

          <input
            type="file"
            id="credentialsFile"
            accept=".json"
            onChange={handleFileUpload}
            disabled={loading}
            style={{ marginBottom: '8px', fontSize: '14px', width: '100%' }}
          />
          {existingSettings && (
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              既に設定されています。変更する場合のみファイルを選択してください。
            </p>
          )}
          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
            または、JSONを直接貼り付けてください：
          </p>
          <textarea
            id="credentialsJson"
            value={credentialsJson}
            onChange={(e) => {
              setCredentialsJson(e.target.value);
              setValidationErrors(prev => ({ ...prev, credentialsJson: undefined }));
            }}
            placeholder='{"type": "service_account", "project_id": "...", ...}'
            disabled={loading}
            rows={8}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: validationErrors.credentialsJson ? '1px solid #e53e3e' : '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
          {validationErrors.credentialsJson && (
            <span style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.credentialsJson}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: loading ? 0.6 : 1
            }}
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: loading ? '#9ca3af' : '#2563eb',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {loading ? '保存中...' : '保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default GASettingsModal;
